/**
 * Search-suggestion expansion for variant-identified course links
 * (variant-identified-course-detail): a course appears once per variant the
 * learner can access, each row linking to that variant's URL — never a
 * single row pointing at an arbitrarily chosen kind.
 */
import type { SearchSuggestion } from '@/types/search';

export interface SuggestionEntry extends SearchSuggestion {
  /** Stable list key — `${id}` for a kindless row, `${id}:${kind}` per variant row. */
  key: string;
  /** The variant this row links as; undefined → bare kindless course URL. */
  linkKind?: string;
}

/**
 * One row per accessible variant. A suggestion whose `variantKinds`
 * intersect the learner's accessible kinds expands to one entry per
 * intersecting kind (in the course's kind order). A suggestion with no
 * kinds or no accessible intersection stays a
 * single kindless entry.
 */
export function expandSuggestionsByAccessibleKinds(
  suggestions: SearchSuggestion[],
  accessibleKinds: ReadonlySet<string>,
): SuggestionEntry[] {
  return suggestions.flatMap((s) => {
    const kinds = (s.variantKinds ?? []).filter((k) => accessibleKinds.has(k));
    if (kinds.length === 0) {
      return [{ ...s, key: s.id }];
    }

    return kinds.map((kind) => ({ ...s, key: `${s.id}:${kind}`, linkKind: kind }));
  });
}
